/* ==========================================================================
   JSMN Bloom — theme.js (vanilla, no build step)
   Replaces: framer-motion (reveal), zustand cartStore + useCartSync (AJAX cart),
   sonner (toast), Radix accordion (<details>), react-router (native links).
   ========================================================================== */
(function () {
  'use strict';

  /* ---------- Utilities ---------- */
  const $ = (sel, ctx) => (ctx || document).querySelector(sel);
  const $$ = (sel, ctx) => Array.from((ctx || document).querySelectorAll(sel));

  function formatMoney(cents, format) {
    format = format || (window.theme && window.theme.moneyFormat) || '${{amount}}';
    const value = (cents / 100).toFixed(2);
    const [whole, dec] = value.split('.');
    const withCommas = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return format
      .replace(/\{\{\s*amount\s*\}\}/, withCommas + '.' + dec)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, withCommas)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.') + ',' + dec)
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/, whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.'));
  }
  window.theme = window.theme || {};
  window.theme.formatMoney = formatMoney;
  function formatMoneyWithCurrency(cents) {
    const code = (window.theme && window.theme.currency) || '';
    return (code ? code + ' ' : '') + (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  /* ---------- Toast (sonner replacement) ---------- */
  const Toast = {
    show(message, type) {
      const root = $('#toast-root');
      if (!root) return;
      const el = document.createElement('div');
      el.className = 'toast' + (type === 'error' ? ' toast--error' : '');
      el.textContent = message;
      root.appendChild(el);
      setTimeout(() => { el.classList.add('is-leaving'); setTimeout(() => el.remove(), 260); }, 2600);
    }
  };
  window.theme.toast = Toast;

  /* ---------- Scroll reveal (framer-motion whileInView replacement) ---------- */
  function initReveal() {
    const els = $$('[data-reveal]');
    if (!els.length) return;
    if (!('IntersectionObserver' in window)) { els.forEach(e => e.classList.add('is-visible')); return; }
    const io = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) { entry.target.classList.add('is-visible'); io.unobserve(entry.target); }
      });
    }, { rootMargin: '0px 0px -50px 0px', threshold: 0.05 });
    els.forEach(el => io.observe(el));
  }

  /* ---------- Sticky header shadow ---------- */
  function initHeader() {
    const header = $('.site-header');
    if (!header) return;
    const onScroll = () => header.classList.toggle('is-scrolled', window.scrollY > 10);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });

    const menu = $('#MobileMenu');
    $$('[data-menu-open]').forEach(b => b.addEventListener('click', () => { if (menu) { menu.setAttribute('open', ''); document.body.style.overflow = 'hidden'; } }));
    $$('[data-menu-close]').forEach(b => b.addEventListener('click', () => { if (menu) { menu.removeAttribute('open'); document.body.style.overflow = ''; } }));
  }

  /* ---------- Cart (zustand cartStore replacement, via Shopify AJAX API) ---------- */
  const Cart = {
    state: null,
    drawer: null,
    async fetch() {
      const res = await fetch(window.theme.routes.cart_url + '.js', { headers: { Accept: 'application/json' } });
      this.state = await res.json();
      this.render();
      return this.state;
    },
    async add(items) {
      this.setLoading(true);
      try {
        const res = await fetch(window.theme.routes.cart_add_url + '.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items })
        });
        if (!res.ok) { const err = await res.json(); throw new Error(err.description || err.message || window.theme.strings.error); }
        await this.fetch();
        Toast.show(window.theme.strings.added);
        this.open();
      } catch (e) {
        Toast.show(e.message || window.theme.strings.error, 'error');
      } finally { this.setLoading(false); }
    },
    async change(key, quantity) {
      this.setLoading(true);
      try {
        const res = await fetch(window.theme.routes.cart_change_url + '.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: key, quantity })
        });
        if (!res.ok) { const err = await res.json(); throw new Error(err.description || window.theme.strings.error); }
        this.state = await res.json();
        this.render();
      } catch (e) { Toast.show(e.message, 'error'); }
      finally { this.setLoading(false); }
    },
    setLoading(on) {
      if (this.drawer) this.drawer.classList.toggle('is-loading', on);
      $$('[data-add-to-cart]').forEach(b => { b.disabled = on; });
    },
    open() {
      if (!this.drawer) return;
      this.drawer.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      const first = $('[data-cart-close]', this.drawer); if (first) first.focus();
    },
    close() {
      if (!this.drawer) return;
      this.drawer.classList.remove('is-open');
      document.body.style.overflow = '';
    },
    render() {
      const cart = this.state; if (!cart) return;
      $$('[data-cart-count]').forEach(el => { el.textContent = cart.item_count; el.setAttribute('data-count', cart.item_count); });
      if (!this.drawer) return;
      const body = $('[data-cart-items]', this.drawer);
      const empty = $('[data-cart-empty]', this.drawer);
      const footer = $('[data-cart-footer]', this.drawer);
      if (cart.item_count === 0) {
        if (body) body.innerHTML = '';
        if (empty) empty.classList.remove('hidden');
        if (footer) footer.classList.add('hidden');
        return;
      }
      if (empty) empty.classList.add('hidden');
      if (footer) footer.classList.remove('hidden');
      if (body) {
        body.innerHTML = cart.items.map(item => `
          <div class="cart-item" data-key="${item.key}">
            <a class="cart-item__image" href="${item.url}">
              ${item.image ? `<img src="${item.image.replace(/(\.[a-z]+)(\?.*)?$/i, '_300x400_crop_center$1$2')}" alt="${(item.product_title || '').replace(/"/g, '&quot;')}" loading="lazy">` : ''}
            </a>
            <div>
              <a class="cart-item__title" href="${item.url}">${item.product_title}</a>
              ${item.variant_title && item.variant_title !== 'Default Title' ? `<div class="cart-item__variant">${item.variant_title}</div>` : ''}
              <div class="cart-item__row">
                <div class="quantity">
                  <button type="button" data-qty-minus aria-label="Decrease">−</button>
                  <input type="number" min="0" value="${item.quantity}" data-qty-input aria-label="Quantity">
                  <button type="button" data-qty-plus aria-label="Increase">+</button>
                </div>
                <div class="cart-item__price">${formatMoney(item.final_line_price)}</div>
              </div>
              <button type="button" class="cart-item__remove" data-remove>${window.theme.strings.remove || 'Remove'}</button>
            </div>
          </div>`).join('');
      }
      const subtotal = $('[data-cart-subtotal]', this.drawer);
      if (subtotal) subtotal.textContent = formatMoney(cart.items_subtotal_price);
      const bar = $('[data-shipping-bar]', this.drawer);
      if (bar) {
        const threshold = parseInt(bar.dataset.threshold, 10) * 100;
        const remaining = Math.max(0, threshold - cart.items_subtotal_price);
        const pct = Math.min(100, Math.round((cart.items_subtotal_price / threshold) * 100));
        $('[data-shipping-fill]', bar).style.width = pct + '%';
        const txt = $('[data-shipping-text]', bar);
        txt.innerHTML = remaining === 0 ? bar.dataset.unlocked : bar.dataset.remaining.replace('[amount]', formatMoney(remaining));
      }
    },
    init() {
      this.drawer = $('#CartDrawer');
      if (this.drawer) {
        this.drawer.addEventListener('click', (e) => {
          if (e.target.closest('[data-cart-close]') || e.target.classList.contains('cart-drawer__backdrop')) this.close();
          const item = e.target.closest('.cart-item'); if (!item) return;
          const key = item.dataset.key;
          const input = $('[data-qty-input]', item);
          if (e.target.closest('[data-qty-minus]')) this.change(key, Math.max(0, parseInt(input.value, 10) - 1));
          if (e.target.closest('[data-qty-plus]')) this.change(key, parseInt(input.value, 10) + 1);
          if (e.target.closest('[data-remove]')) this.change(key, 0);
        });
        this.drawer.addEventListener('change', (e) => {
          if (e.target.matches('[data-qty-input]')) this.change(e.target.closest('.cart-item').dataset.key, Math.max(0, parseInt(e.target.value, 10) || 0));
        });
        document.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close(); });
      }
      $$('[data-cart-open]').forEach(b => b.addEventListener('click', (e) => {
        if (this.drawer && document.body.dataset.cartType !== 'page') { e.preventDefault(); this.open(); }
      }));
      // Product forms (product page + product cards)
      document.addEventListener('submit', (e) => {
        const form = e.target.closest('form[data-product-form]'); if (!form) return;
        e.preventDefault();
        const id = form.querySelector('[name="id"]').value;
        const qtyEl = form.querySelector('[name="quantity"]');
        const quantity = qtyEl ? parseInt(qtyEl.value, 10) || 1 : 1;
        this.add([{ id: parseInt(id, 10), quantity }]);
      });
      this.fetch();
    }
  };
  window.theme.cart = Cart;

  /* ---------- Variant picker (pills) ---------- */
  function initVariantPickers() {
    $$('[data-variant-picker]').forEach(picker => {
      let variants;
      try { variants = JSON.parse($('[data-variants-json]', picker).textContent); } catch (e) { return; }
      const form = picker.closest('form[data-product-form]') || $(`form[data-product-form][data-product-id="${picker.dataset.productId}"]`);
      const idInput = form ? form.querySelector('[name="id"]') : null;
      const button = form ? form.querySelector('[data-add-to-cart]') : null;
      const priceEl = $(`[data-price][data-product-id="${picker.dataset.productId}"]`) || $('[data-price]', picker.closest('.product-card, .product__info') || picker);

      function selectedOptions() {
        return $$('[data-option-index]', picker).map(group => {
          const active = $('.variant-pill.is-active', group);
          return active ? active.dataset.value : null;
        });
      }
      function update() {
        const opts = selectedOptions();
        const match = variants.find(v => v.options.every((o, i) => o === opts[i]));
        if (!match) return;
        if (idInput) idInput.value = match.id;
        if (button) {
          button.disabled = !match.available;
          const label = $('[data-atc-text]', button) || button;
          label.textContent = match.available ? window.theme.strings.addToCart : window.theme.strings.soldOut;
        }
        if (priceEl) {
          let html;
          if (priceEl.dataset.priceStyle === 'showcase') {
            html = '';
            if (match.compare_at_price && match.compare_at_price > match.price) html += `<span class="price__compare">${formatMoneyWithCurrency(match.compare_at_price)}</span>`;
            html += `<span class="showcase__price-now">${formatMoneyWithCurrency(match.price)}</span>`;
          } else {
            html = formatMoney(match.price);
            if (match.compare_at_price && match.compare_at_price > match.price) html += ` <span class="price__compare">${formatMoney(match.compare_at_price)}</span>`;
          }
          priceEl.innerHTML = html;
        }
        if (picker.dataset.updateUrl === 'true' && history.replaceState) {
          const url = new URL(window.location.href); url.searchParams.set('variant', match.id); history.replaceState({}, '', url);
        }
      }
      picker.addEventListener('click', (e) => {
        const pill = e.target.closest('.variant-pill'); if (!pill) return;
        e.preventDefault();
        $$('.variant-pill', pill.parentElement).forEach(p => { p.classList.remove('is-active'); p.setAttribute('aria-pressed', 'false'); });
        pill.classList.add('is-active'); pill.setAttribute('aria-pressed', 'true');
        update();
      });
    });
    // Quantity steppers on product page
    $$('.quantity[data-quantity]').forEach(q => {
      const input = $('input', q);
      $('[data-qty-minus]', q).addEventListener('click', () => { input.value = Math.max(1, parseInt(input.value, 10) - 1); });
      $('[data-qty-plus]', q).addEventListener('click', () => { input.value = parseInt(input.value, 10) + 1; });
    });
  }

  /* ---------- Announcement marquee ---------- */
  function initMarquee() {
    $$('.announcement-bar__track.is-marquee').forEach(track => {
      // duplicate content so translateX(-50%) loops seamlessly
      const html = track.innerHTML;
      track.innerHTML = html + html;
    });
  }

  /* ---------- Welcome popup ---------- */
  function initPopup() {
    const p = $('#WelcomePopup'); if (!p) return;
    const key = 'jsmn_popup_seen';
    const close = () => { p.classList.remove('is-open'); try { localStorage.setItem(key, String(Date.now())); } catch (e) {} };
    $$('[data-popup-close]', p).forEach(b => b.addEventListener('click', close));
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') close(); });
    if (p.classList.contains('is-success')) return; // server rendered success state
    let seen = null; try { seen = localStorage.getItem(key); } catch (e) {}
    const days = parseInt(p.dataset.days, 10) || 7;
    if (seen && Date.now() - parseInt(seen, 10) < days * 864e5) return;
    if (window.Shopify && window.Shopify.designMode) return;
    setTimeout(() => p.classList.add('is-open'), (parseInt(p.dataset.delay, 10) || 0) * 1000);
  }

  /* ---------- Before / after slider ---------- */
  function initBeforeAfter() {
    $$('[data-before-after]').forEach(frame => {
      const before = $('[data-ba-before]', frame), handle = $('[data-ba-handle]', frame);
      const img = $('.ba__img--before', frame);
      const sync = () => { if (img) img.style.width = frame.getBoundingClientRect().width + 'px'; };
      sync(); window.addEventListener('resize', sync);
      let dragging = false;
      const set = (clientX) => {
        const r = frame.getBoundingClientRect();
        const pct = Math.max(0, Math.min(100, ((clientX - r.left) / r.width) * 100));
        before.style.width = pct + '%'; handle.style.left = pct + '%';
      };
      frame.addEventListener('pointerdown', (e) => { dragging = true; frame.setPointerCapture(e.pointerId); set(e.clientX); });
      frame.addEventListener('pointermove', (e) => { if (dragging) set(e.clientX); });
      frame.addEventListener('pointerup', () => { dragging = false; });
      frame.addEventListener('pointercancel', () => { dragging = false; });
    });
  }

  /* ---------- Tabbed carousels (ingredients + hair journey) ---------- */
  function initTabs(rootSel, opts) {
    $$(rootSel).forEach(root => {
      const tabs = $$('[data-tab]', root), panels = $$('[data-panel]', root);
      const n = panels.length; if (!n) return;
      let idx = 0, timer = null;
      const go = (i) => {
        idx = (i + n) % n;
        tabs.forEach(t => { const on = parseInt(t.dataset.tab, 10) === idx; t.classList.toggle('is-active', on); t.setAttribute('aria-selected', on); });
        panels.forEach(p => p.classList.toggle('is-active', parseInt(p.dataset.panel, 10) === idx));
        const fill = $('[data-journey-fill]', root); if (fill) fill.style.width = ((idx + 1) / n * 100) + '%';
      };
      tabs.forEach(t => t.addEventListener('click', () => { go(parseInt(t.dataset.tab, 10)); restart(); }));
      const prev = $('[data-journey-prev]', root), next = $('[data-journey-next]', root);
      if (prev) prev.addEventListener('click', () => go(idx - 1));
      if (next) next.addEventListener('click', () => go(idx + 1));
      // touch swipe
      let sx = null;
      root.addEventListener('touchstart', (e) => { sx = e.touches[0].clientX; }, { passive: true });
      root.addEventListener('touchend', (e) => { if (sx === null) return; const dx = sx - e.changedTouches[0].clientX; if (Math.abs(dx) > 50) go(dx > 0 ? idx + 1 : idx - 1); sx = null; });
      const restart = () => { if (timer) clearInterval(timer); if (opts.autoplay && root.dataset.autoplay !== 'false') timer = setInterval(() => go(idx + 1), opts.interval || 4000); };
      restart();
    });
  }

  /* ---------- Loox hook (reviews) ---------- */
  function initLoox() {
    const w = window; if (w.loox && typeof w.loox.reload === 'function') { w.loox.reload(); setTimeout(() => w.loox.reload(), 800); }
  }

  /* ---------- Theme editor support ---------- */
  document.addEventListener('shopify:section:load', () => { initReveal(); initVariantPickers(); initMarquee(); initBeforeAfter(); initTabs('[data-ingredient-tabs]', { autoplay: true, interval: 4000 }); initTabs('[data-journey]', { autoplay: false }); });

  document.addEventListener('DOMContentLoaded', () => {
    initReveal();
    initHeader();
    Cart.init();
    initVariantPickers();
    initMarquee();
    initPopup();
    initBeforeAfter();
    initTabs('[data-ingredient-tabs]', { autoplay: true, interval: 4000 });
    initTabs('[data-journey]', { autoplay: false });
    initLoox();
  });
})();
